import React, { useEffect, useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { io } from "socket.io-client";

import { loginUser, resendVerificationEmail } from "../api/authApi";
import { refreshMenuCache } from "../api/menuApi";
import "react-toastify/dist/ReactToastify.css";
import useConfig from "../hooks/useConfig";
import { loadPublicConfigs } from "../redux/slices/configSlice";
import { setupSessionExpirySync } from "../services/sessionService";
import socket from "../socket";

import { notifyUser } from "../utils/notifyUser";

const web_socket = io(process.env.REACT_APP_API_SOCKET_URL);

const verificationToastContent = (title, message) => (
    <div>
        <h4 className="font-display font-semibold text-navy">{title}</h4>
        <div className="mt-1 font-body text-sm text-navy/70">{message}</div>
    </div>
);

const Login = () => {
    const [usernameOrEmail, setUsernameOrEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [verificationState, setVerificationState] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { getConfig } = useConfig();
    const googleEnabled =
        getConfig("GOOGLE_AUTH_ENABLED")?.toLowerCase() === "true";
    const microsoftEnabled =
        getConfig("MICROSOFT_AUTH_ENABLED")?.toLowerCase() === "true";

    // Load public configs when component mounts
    useEffect(() => {
        dispatch(loadPublicConfigs()).then(() => { });
    }, [dispatch]);

    useEffect(() => {
        document.title = "login | AQI";
    }, []);

    useEffect(() => {
        let cancelled = false;

        const redirectIfSessionIsValid = async () => {
            if(localStorage.getItem("userLoggedIn") !== "true") return;

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/user`,
                    {
                        method: "GET",
                        credentials: "include",
                    },
                );

                if(!response.ok || cancelled) return;

                const userData = await response.json();
                const redirectPath =
                    userData?.role === "admin"
                        ? "/home"
                        : userData?.role === "investor"
                            ? userData.isProfileVerified
                                ? "/dashboard"
                                : "/investor-account-creation"
                            : userData?.role === "entrepreneur"
                                ? userData.isProfileVerified
                                    ? "/dashboard"
                                    : "/entrepreneur-account-creation"
                                : "/unauthorized";

                navigate(redirectPath, { replace: true });
            } catch(_error) {
                // Keep the login form available when the session check fails.
            }
        };

        redirectIfSessionIsValid();

        return () => {
            cancelled = true;
        };
    }, [navigate]);

    useEffect(() => {
        if(
            !verificationState ||
            verificationState.canResend ||
            verificationState.retryAfterSeconds <= 0
        ) {
            return undefined;
        }

        const timer = setInterval(() => {
            setVerificationState((current) => {
                if(!current || current.canResend) return current;

                const retryAfterSeconds = Math.max(
                    0,
                    current.retryAfterSeconds - 1,
                );
                return {
                    ...current,
                    retryAfterSeconds,
                    canResend: retryAfterSeconds === 0,
                };
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [verificationState]);

    // Show toast for OAuth errors passed as query params
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const error = params.get("error");
        if (error === "account_disabled") {
            // Delay toast so it fires AFTER useClearToastsOnRouteChange (parent effect)
            // has already run its toast.dismiss(), otherwise the toast gets cleared instantly
            setTimeout(() => {
                notifyUser(null, "error", "You cannot log in as your account is currently disabled. If you need further assistance, please contact support.", "toast", null, null, 8000, null, null, false, false, "top-center");
            }, 300);
            window.history.replaceState({}, "", "/login");
        }
    }, [location.search]);

    // Show simple verification toast if navigated from registration
    useEffect(() => {
        const toastText = location.state?.verificationToastText;
        if (toastText) {
            // small delay so parent toasts/dismissals run first
            setTimeout(() => {
                notifyUser(
                    null,
                    "success",
                    verificationToastContent(
                        "Check Your Email to Continue",
                        toastText,
                    ),
                    "toast",
                    null,
                    null,
                    8000,
                    null,
                    null,
                    false,
                    false,
                    "top-center",
                );
                // remove state so refreshing or returning doesn't replay it
                try {
                    window.history.replaceState({}, "", "/login");
                } catch (e) {
                    // ignore
                }
            }, 200);
        }
    }, [location.state]);

    const handleLogin = async () => {
        if (!usernameOrEmail || !password) {
            notifyUser(null, "error", "Please enter email and password!", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setVerificationState(null);
        setLoading(true);
        try {
            const result = await loginUser(usernameOrEmail, password);

            if (result.error) {
                if(result.code === "EMAIL_NOT_VERIFIED") {
                    setVerificationState({
                        identifier: usernameOrEmail,
                        canResend: Boolean(result.canResend),
                        retryAfterSeconds: result.retryAfterSeconds || 0,
                    });
                    notifyUser(
                        null,
                        "error",
                        verificationToastContent(
                            "Verify Your Email to Continue",
                            result.error,
                        ),
                        "toast",
                        null,
                        null,
                        null,
                        null,
                        null,
                        false,
                        false,
                        "top-center",
                    );
                } else if (result.code === "ACCOUNT_DISABLED") {
                    notifyUser(null, "error", "You cannot log in as your account is currently disabled. If you need further assistance, please contact support.", "toast", null, null, 8000, null, null, false, false, "top-center");
                } else if (result.error === "Please contact support") {
                    notifyUser(null, "error", "You are not part of the required group. Please contact support.", "toast", null, null, null, null, null, false, false, "top-center");
                } else if (result.error === "Your account has been deleted.") {
                    notifyUser(null, "error", "Your account has been deleted.", "toast", null, null, null, null, null, false, false, "top-center");
                } else {
                    notifyUser(null, "error", result.error, "toast", null, null, null, null, null, false, false, "top-center");
                }
                return;
            }

            setVerificationState(null);

            // Set session data
            localStorage.setItem("session_id", result.token);
            localStorage.setItem("userLoggedIn", "true");
            setupSessionExpirySync();

            // Fetch decoded user from the secure cookie for additional validation
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/user`,
                {
                    method: "GET",
                    credentials: "include",
                },
            );

            if (!response.ok) {
                notifyUser(null, "error", "Failed to retrieve user session.", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }

            const data = await response.json();
            const { id, role, isProfileVerified } = data;

            // WebSocket and cache
            web_socket.emit("login", id);
            window.dispatchEvent(new Event("user-updated"));
            await refreshMenuCache(id);
            socket.emit("menu-update");

            const getRedirectPath = () => {
                if (role === "admin") {
                    return "/home";
                }
                if (role === "investor") {
                    return isProfileVerified
                        ? "/dashboard"
                        : "/investor-account-creation";
                }
                if (role === "entrepreneur") {
                    return isProfileVerified
                        ? "/dashboard"
                        : "/entrepreneur-account-creation";
                }
                return "/unauthorized";
            };
            window.dispatchEvent(
                new CustomEvent("user-logged-in", { detail: data }),
            );

            /* tiny delay lets App.js process the event before the route guard runs */
            setTimeout(() => {
                const redirectSource = localStorage.getItem("redirectSource");
                const redirectPath = localStorage.getItem("redirectAfterLogin");
                const accountSubmitted = !!result.user?.application_submitted;

                if (
                    redirectSource === "campaign" &&
                    redirectPath &&
                    accountSubmitted
                ) {
                    localStorage.removeItem("redirectSource");
                    localStorage.removeItem("redirectAfterLogin");
                    navigate(redirectPath);
                } else {
                    navigate(getRedirectPath());
                }
            }, 50);
        } catch {
            notifyUser(null, "error", "An unexpected error occurred during login.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setLoading(false);
        }
    };

    const handleResendVerification = async() => {
        if(!verificationState?.canResend || resendLoading) return;

        setResendLoading(true);
        const result = await resendVerificationEmail(
            verificationState.identifier,
        );
        setResendLoading(false);

        if(result.error) {
            setVerificationState((current) => current && ({
                ...current,
                canResend: Boolean(result.canResend),
                retryAfterSeconds:
                    result.retryAfterSeconds ?? current.retryAfterSeconds,
            }));
            notifyUser(null, "error", result.error, "toast", null, null, null, null, 350, false, false, "top-center");
            return;
        }

        setVerificationState((current) => current && ({
            ...current,
            canResend: false,
            retryAfterSeconds: result.retryAfterSeconds || 0,
        }));
        notifyUser(
            null,
            "success",
            verificationToastContent(
                "Check Your Email to Continue",
                result.message,
            ),
            "toast",
            null,
            null,
            null,
            null,
            null,
            false,
            false,
            "top-center",
        );
    };

    // Always join user room and listen for force_logout
    useEffect(() => {
        // On mount, if user is logged in, emit login event to join room
        const userId = localStorage.getItem("userId");
        if (userId) {
            web_socket.emit("login", userId);
        }
        web_socket.on("force_logout", (data) => {
            // Clear user session
            localStorage.removeItem("userToken");
            notifyUser(null, "error", data.message || "You have been logged out", "toast", null, null, null, null, null, false, false, "top-center");
            navigate("/login");
        });

        return () => {
            web_socket.off("force_logout");
        };
    }, [navigate]);

    return (
        <div className="flex min-h-screen items-center justify-center bg-paper px-4 font-body text-navy sm:px-6 lg:px-8">
            <div className="w-full max-w-md rounded-2xl border border-navy/10 bg-white p-8 shadow-[0_20px_60px_rgba(11,31,58,0.12)]">
                <div className="flex flex-col items-center gap-3 mb-8">
                    <div
                        className="flex h-24 w-24 cursor-pointer items-center justify-center overflow-hidden rounded-2xl border border-gold/40 bg-navy shadow-lg shadow-navy/20 transition-transform duration-200 hover:scale-[1.03]"
                        onClick={() => (window.location.href = `${process.env.REACT_APP_FRONTEND_PUBLIC_URL}/`)}
                        tabIndex={0}
                        role="button"
                        aria-label="Go to Home"
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                                window.location.href = `${process.env.REACT_APP_FRONTEND_PUBLIC_URL}/`;
                        }}
                    >
                        <img
                            src={`/${process.env.REACT_APP_LOGO_WHITE
                                }`}
                            alt="AQi Logo"
                            className="w-20 h-auto object-contain"
                            loading="lazy"
                        />
                    </div>
                </div>

                <h2 className="mb-6 text-center font-display text-2xl font-bold tracking-tight text-navy">
                    Log In to Your Account
                </h2>

                <form
                    className="space-y-4"
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            handleLogin();
                        }
                    }}
                >
                    <input
                        type="text"
                        className="w-full rounded-lg border border-navy/15 bg-paper/60 px-4 py-2.5 text-navy placeholder:text-navy/40 transition-colors focus:border-gold focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold/20"
                        placeholder="Enter Email"
                        value={usernameOrEmail}
                        onChange={(e) => setUsernameOrEmail(e.target.value)}
                    />

                    <div className="relative">
                        <input
                            type={showPassword ? "text" : "password"}
                            className="w-full rounded-lg border border-navy/15 bg-paper/60 px-4 py-2.5 pr-10 text-navy placeholder:text-navy/40 transition-colors focus:border-gold focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold/20"
                            placeholder="Enter Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <button
                            type="button"
                            aria-label={
                                showPassword ? "Hide password" : "Show password"
                            }
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute right-3 top-1/2 m-0 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-navy/50 transition-colors hover:text-gold focus:outline-none"
                            tabIndex={0}
                        >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                    </div>

                    <div className="flex justify-end text-sm mt-1">
                        {/* <a
              href="/forgot-username"
              className="text-navy hover:underline"
            >
              Forgot Username?
            </a> */}
                        <a
                            href="/forgot-password"
                            className="font-medium text-navy transition-colors hover:text-gold hover:underline"
                        >
                            Forgot Password?
                        </a>
                    </div>

                    <button
                        onClick={handleLogin}
                        type="button"
                        className="w-full rounded-lg bg-navy py-2.5 font-semibold text-white transition-colors duration-200 hover:bg-navy-2 focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={loading}
                    >
                        {loading ? "Logging in..." : "Login"}
                    </button>

                    {verificationState && (
                        <div className="text-center text-sm text-navy/60">
                            {verificationState.canResend && (
                                <button
                                    type="button"
                                    className="mt-3 w-full rounded-lg border border-navy bg-white py-2.5 font-semibold text-navy transition-colors duration-200 hover:bg-navy hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                                    disabled={resendLoading}
                                    onClick={handleResendVerification}
                                >
                                    {resendLoading
                                        ? "Sending..."
                                        : "Resend Verification Email"}
                                </button>
                            )}
                        </div>
                    )}
                </form>

                <div className="text-center mt-6">
                    <div className="flex justify-center gap-4">
                        {googleEnabled && (
                            <button
                                onClick={() =>
                                    (window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google?intent=login`)
                                }
                                className="flex h-12 w-12 items-center justify-center rounded-full border border-navy/15 bg-white shadow-sm transition-all hover:scale-105 hover:border-gold hover:bg-paper"
                            >
                                <img
                                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                                    alt="Google Logo"
                                    className="w-6 h-6"
                                />
                            </button>
                        )}
                        {microsoftEnabled && (
                            <button
                                onClick={() =>
                                    (window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/microsoft?intent=login`)
                                }
                                className="flex h-12 w-12 items-center justify-center rounded-full border border-navy/15 bg-white shadow-sm transition-all hover:scale-105 hover:border-gold hover:bg-paper"
                            >
                                <img
                                    src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                                    alt="Microsoft Logo"
                                    className="w-6 h-6"
                                />
                            </button>
                        )}
                    </div>
                </div>

                <p className="mt-6 text-center text-sm text-navy/60">
                    Don&apos;t have an account?{" "}
                    <a
                        href="/sign-up"
                        className="font-semibold text-navy transition-colors hover:text-gold hover:underline"
                    >
                        Register
                    </a>
                </p>
            </div>
        </div>
    );
};

export default Login;
